import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Check, Loader2, Gift, AlertTriangle, Clock } from 'lucide-react';
import { openChest } from '../lib/api';
import { useApp } from '../App';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const ChestModal = ({ chestNumber, open, onClose, onStateChange }) => {
  const { fetchCart, fetchRaffles, selectedRaffleId } = useApp();
  const [chestData, setChestData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [revealing, setRevealing] = useState(true);
  const confettiTriggered = useRef(false);

  useEffect(() => {
    if (open && chestNumber !== null && chestNumber !== undefined && selectedRaffleId) {
      setChestData(null);
      setRevealing(true);
      confettiTriggered.current = false;
      loadAndReserveChest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, chestNumber]);

  const loadAndReserveChest = async () => {
    setLoading(true);
    try {
      // Opening = auto-reserve on backend
      const res = await openChest(chestNumber, selectedRaffleId);
      setTimeout(() => {
        setChestData(res.data);
        setRevealing(false);
        triggerConfetti(res.data.is_free);
        // Update cart since chest is now reserved
        fetchCart();
        fetchRaffles();
        if (onStateChange) onStateChange(chestNumber, 'reserved');
      }, 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al abrir el cofre');
      onClose();
    }
    setLoading(false);
  };

  const triggerConfetti = async (isFree) => {
    if (confettiTriggered.current) return;
    confettiTriggered.current = true;
    try {
      const confetti = (await import('canvas-confetti')).default;
      confetti({
        particleCount: isFree ? 150 : 80,
        spread: isFree ? 100 : 60,
        origin: { y: 0.6 },
        colors: isFree ? ['#35D07F', '#FFC640', '#4BA3FF', '#F7F2E7', '#FFB347'] : ['#FFC640', '#D4AF37', '#FFB347', '#F7F2E7'],
      });
    } catch { }
  };

  const handleAccept = () => {
    const label = `#${String(chestNumber).padStart(4, '0')}`;
    if (chestData?.is_free) {
      toast.success(`Cofre ${label} GRATIS apartado`);
    } else {
      toast.success(`Cofre ${label} apartado por 10 minutos`);
    }
    onClose();
  };

  const isFree = chestData?.is_free || chestData?.price === 0;
  const price = chestData?.price;

  return (
    <Dialog open={open} onOpenChange={() => { /* prevent closing by clicking outside during reveal */ if (!revealing) handleAccept(); }}>
      <DialogContent className="bg-[#0E1016] border border-white/[0.08] max-w-sm mx-auto p-0 overflow-hidden [&>button]:hidden" aria-describedby={undefined}>
        <div className="sr-only"><h2>Cofre #{String(chestNumber).padStart(4, '0')}</h2></div>
        <div className="relative">
          {/* Header gradient */}
          <div className={`h-28 flex items-center justify-center ${
            !revealing && isFree
              ? 'bg-gradient-to-b from-[rgba(53,208,127,0.15)] to-transparent'
              : 'bg-gradient-to-b from-[rgba(255,198,64,0.12)] to-transparent'
          }`}>
            <motion.div
              initial={{ scale: 1 }}
              animate={revealing ? { rotateY: [0, 180, 360], scale: [1, 1.1, 1] } : { scale: 1 }}
              transition={{ duration: 0.8, ease: 'easeInOut' }}
            >
              {revealing ? (
                <div className="w-18 h-18 rounded-xl bg-[rgba(255,198,64,0.15)] border-2 border-[#FFC640] flex items-center justify-center shimmer-gold" style={{width:72,height:72}}>
                  <span className="text-3xl text-[#FFC640]">?</span>
                </div>
              ) : isFree ? (
                <div className="w-18 h-18 rounded-xl bg-[rgba(53,208,127,0.15)] border-2 border-[#35D07F] flex items-center justify-center" style={{width:72,height:72}}>
                  <Gift size={32} className="text-[#35D07F]" />
                </div>
              ) : (
                <div className="w-18 h-18 rounded-xl bg-[rgba(255,198,64,0.2)] border-2 border-[#FFC640] flex items-center justify-center" style={{width:72,height:72}}>
                  <span className="text-2xl text-[#FFC640]">$</span>
                </div>
              )}
            </motion.div>
          </div>

          <div className="p-5 pt-1 text-center">
            <h3 className="text-sm text-[#F7F2E7]/60 font-medium mb-1">
              Cofre #{String(chestNumber).padStart(4, '0')}
            </h3>

            {revealing ? (
              <div className="py-6">
                <Loader2 size={28} className="animate-spin text-[#FFC640] mx-auto" />
                <p className="text-[#F7F2E7]/50 text-sm mt-3">Abriendo cofre...</p>
              </div>
            ) : (
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: 'spring', damping: 15, stiffness: 300 }}
                >
                  {isFree ? (
                    <>
                      <p className="text-xs text-[#35D07F] uppercase tracking-wider mb-1 font-semibold">COFRE GRATIS</p>
                      <p className="text-4xl font-heading font-bold text-[#35D07F] tabular-nums">$0.00</p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-[#FFC640] uppercase tracking-wider mb-1">Valor del cofre</p>
                      <p className="text-4xl font-heading font-bold text-[#FFC640] tabular-nums">${price?.toFixed(2)}</p>
                    </>
                  )}

                  {/* Reserved notice */}
                  <div className="mt-4 p-3 bg-[rgba(75,163,255,0.08)] border border-[rgba(75,163,255,0.2)] rounded-xl">
                    <p className="text-xs text-[#4BA3FF] flex items-center justify-center gap-1.5">
                      <Clock size={14} />
                      Este cofre ha sido apartado para ti por 10 minutos.
                    </p>
                  </div>

                  {/* Anti-mapping warning */}
                  <div className="mt-3 p-2.5 bg-[rgba(255,180,50,0.06)] border border-[rgba(255,180,50,0.15)] rounded-xl">
                    <p className="text-[10px] text-amber-400/70 flex items-start gap-1.5">
                      <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                      Si abres y no completas el pago de 3 cofres, tu cuenta será bloqueada por 2 horas.
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>
            )}

            {!revealing && (
              <div className="mt-5">
                <Button
                  onClick={handleAccept}
                  className={`w-full h-12 font-heading font-bold text-base rounded-xl shadow-[0_10px_28px_rgba(0,0,0,0.45)]
                    ${isFree ? 'bg-[#35D07F] text-white hover:bg-[#2cb86e]' : 'bg-[#FFC640] text-[#090A0D] hover:bg-[#FFD36B]'}`}
                  data-testid="chest-accept-button"
                >
                  <Check size={18} className="mr-2" />
                  ACEPTAR Y CONTINUAR
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChestModal;
